import React from "react";
import { SliceComponentProps } from "@prismicio/react";
import { Slice } from "@prismicio/types";
import {
  ComposableSliceSliceFragment,
  PrismicReusableContent,
  PrismicReusableContentDataBodyRichTextV2,
  PrismicReusableContentDataBodyTextAndLinkCta,
  PrismicReusableContentDataBodyContentCarousel,
  PrismicReusableContentData,
  PrismicReusableContentDataBody,
  PrismicStandardLandingPageDataBodyComposableSlicePrimary,
  PrismicStandardLandingPageDataBodyComposableSliceItem,
  PrismicLinkField,
  PrismicReusableContentDataBodyIconAndText,
  PrismicReusableContentDataBodyHoverCard,
  PrismicReusableContentDataBodyTextAndIcons,
  PrismicReusableContentDataBodyTextAndImage,
  PrismicReusableContentDataBodyImageGrid,
  PrismicReusableContentDataBodyImageTextAndCta,
  PrismicReusableContentDataBodyInstantQuote,
  PrismicReusableContentDataBodyTalkToAnExpert,
} from "../../types/graphql";
import { IPageContext } from "../../types/types";
import { SharedSliceType } from "../shared-slice/shared-slice";
import "slick-carousel/slick/slick.css";
import "slick-carousel/slick/slick-theme.css";
import clsx from "clsx";

// Modules
import Carousel from "../carousel/carousel";
import HoverCard from "../modules/hover-card";
import IconAndText from "../modules/icon-and-text";
import ImageGrid from "../modules/image-grid";
import ImageTextAndCta from "../modules/image-text-and-cta";
import RichText from "../modules/rich-text";
import TextAndIcons from "../modules/text-and-icons";
import TextAndImage from "../modules/text-and-image";
import TextAndLinkCta from "../modules/text-and-link-cta";
import InstantQuote from "../modules/instant-quote";
import TalkToAnExpert from "../modules/talk-to-an-expert";

export type IComposableSliceProps = Slice<
  SharedSliceType,
  ComposableSliceSliceFragment["primary"],
  ComposableSliceSliceFragment["items"][number]
> & { type: SharedSliceType.composable_slice };

const ComposableSlice = ({
  slice,
}: SliceComponentProps<IComposableSliceProps, IPageContext>) => {
  const { primary, items } = slice;

  const primaryContent =
    slice?.primary as PrismicStandardLandingPageDataBodyComposableSlicePrimary;
  const repeatableItems =
    items as PrismicStandardLandingPageDataBodyComposableSliceItem[];

  const backgroundImage =
    primary?.background_image && primary?.background_image?.url;

  // Returns component based on slice_type
  const getModule = (content: PrismicLinkField, cssClass: string) => {
    const document = content?.document as PrismicReusableContent;
    const data = document?.data as PrismicReusableContentData;
    const bodyItems = data?.body as PrismicReusableContentDataBody[];

    const slices = bodyItems?.map((body) => {
      switch (body?.slice_type) {
        case "content_carousel":
          const contentCarouselSlice =
            body as PrismicReusableContentDataBodyContentCarousel;
          return <Carousel slice={contentCarouselSlice} />;

        case "icon_and_text":
          const iconAndTextSlice =
            body as PrismicReusableContentDataBodyIconAndText;
          return <IconAndText slice={iconAndTextSlice} />;

        case "hover_card":
          const hoverCardSlice =
            body as PrismicReusableContentDataBodyHoverCard;
          return <HoverCard slice={hoverCardSlice} />;

        case "rich_text_v2":
          const richTextSlice =
            body as PrismicReusableContentDataBodyRichTextV2;
          return <RichText slice={richTextSlice} />;

        case "text_and_link_cta":
          const textAndLinkCtaSlice =
            body as PrismicReusableContentDataBodyTextAndLinkCta;
          return <TextAndLinkCta slice={textAndLinkCtaSlice} />;

        case "text_and_icons":
          const textAndIconsSlice =
            body as PrismicReusableContentDataBodyTextAndIcons;
          return <TextAndIcons slice={textAndIconsSlice} />;

        case "text_and_image":
          const textAndImageSlice =
            body as PrismicReusableContentDataBodyTextAndImage;
          return <TextAndImage slice={textAndImageSlice} />;

        case "image_grid":
          const imageGridSlice =
            body as PrismicReusableContentDataBodyImageGrid;
          return <ImageGrid slice={imageGridSlice} />;

        case "image_text_and_cta":
          const imageTextAndCtaSlice =
            body as PrismicReusableContentDataBodyImageTextAndCta;
          return <ImageTextAndCta slice={imageTextAndCtaSlice} />;

        case "instant_quote":
          const instantQuoteSlice =
            body as PrismicReusableContentDataBodyInstantQuote;
          return <InstantQuote slice={instantQuoteSlice} />;

        case "talk_to_an_expert":
          const talkToAnExpertSlice =
            body as PrismicReusableContentDataBodyTalkToAnExpert;
          return <TalkToAnExpert slice={talkToAnExpertSlice} />;

        default:
          return <></>;
      }
    });

    return <div className={data?.content_css_class || ""}>{slices}</div>;
  };

  return (
    <div
      className={clsx(["slice-section", primary?.slice_css_class])}
      style={{
        backgroundImage: backgroundImage ? `url(${backgroundImage})` : "",
      }}
    >
      <div className="container">
        {primaryContent && (
          <>
            <div className={primaryContent.primary_content_css_class || ""}>
              {primaryContent.primary_content &&
                getModule(
                  primaryContent.primary_content,
                  primaryContent.primary_content_css_class || ""
                )}
            </div>
            <div className={primaryContent.secondary_content_css_class || ""}>
              {primaryContent.secondary_content &&
                getModule(
                  primaryContent.secondary_content,
                  primaryContent.secondary_content_css_class || ""
                )}
            </div>
          </>
        )}
        {repeatableItems && (
          <div className={primary?.repeatable_content_css_class || ""}>
            {repeatableItems?.map((item) => {
              if (item && item.repeatable_content) {
                return (
                  <div className={item?.repeatable_content_css_class || ""}>
                    {getModule(
                      item?.repeatable_content,
                      item?.repeatable_content_css_class || ""
                    )}
                  </div>
                );
              }
            })}
          </div>
        )}
      </div>
    </div>
  );
};

export default ComposableSlice;
